import React, { Component } from 'react';
import * as Actions from './actions';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { withRouter, Redirect } from 'react-router-dom';
import { getQueryParams } from 'global/js/helpers';
import { globalData } from 'global/js/globalData';
import Error from 'components/Error/Error';
import Header from 'components/Header/Header';
import LocationComponentList from 'components/LocationComponentList/LocationComponentList';
import './home.scss';

class Home extends Component {
    constructor(props) {
        super(props);
    }

    componentDidMount() {
        const prodID = getQueryParams('prodtourID');
        const individualID = getQueryParams('individualID');
        const encryptedParams = getQueryParams('e', false); //ensure that encrypted params aren't altered
        const token = getQueryParams('t', false)

        /*The query string parameters will be encrypted only in cases where there is an
        * incomplete survery that needs NPS/overview questions completed so we're branching
        * here to avoid conflicts with the base-case code path
        */
        if (this.shouldLoadBackupSurvey) {
            // Set the user/prod IDs as soon as possible.
            this.registerEncryptedSurveyIdsWithAppState(encryptedParams);
            this.registerEncryptedTokenWithAppState(token);
            this.fetchDataEncrypted(encryptedParams, token);
        } else {
            // Set the user/prod IDs as soon as possible.
            this.registerSurveyIdsWithAppState(individualID, prodID);

            // Check for unsaved submissions if there is survey data in memory.
            if (this.props.isDataFetched && !this.shouldLoadBackupSurvey) {
                this.checkDirtySubmissions(prodID, individualID, token);
            } else {
                this.fetchData(prodID, individualID);
            }
        }
    }

    registerSurveyIdsWithAppState(individualID, prodTourID) {
        this.props.actions.setUserIDs({
            individualID: individualID,
            prodTourID: prodTourID
        });
    }

    registerEncryptedSurveyIdsWithAppState(encryptedIDs) {
        this.props.actions.setEncryptedUserIDs({ encryptedIDs });
    }

    registerEncryptedTokenWithAppState(token) {
        this.props.actions.setEncryptedToken(token);
    }

    /**
     * Called whenever this page renders. This function loops through the stay and
     * overall submissions and checks if any of them have been edited since they
     * were last sent. Any submissions that have not been sent are then sent back.
     *
     * @param {Integer} prodID
     * @param {Integer} individualID
     * @param {String} token
     */
    checkDirtySubmissions = (prodID, individualID, token) => {
        let wasDirtySubmissionFound = false;

        // Helper function to check connection before we sending 
        // so we don't always show an error message.
        const checkAndSend = (endPoint, data) => {
            wasDirtySubmissionFound = true;

            if (navigator.onLine) {
                this.props.actions.sendSurveyData(
                    this.props.prodID, 
                    this.props.individualID,
                    prodID,
                    individualID, 
                    endPoint, 
                    data, 
                    true,
                    true,
                    token
                );
            }
        };
        
        // Check all of the stays for dirty submissions.
        for (const stay of this.props.surveyData.StaySubmissions)
            if (stay.Submission.isDirty)
                checkAndSend('/stay', stay);

        // Check the overall questions for dirty submissions.
        if (this.props.surveyData.OverallSubmission.Submission.isDirty)
            checkAndSend('/overall', this.props.surveyData.OverallSubmission);

        // Fetch data if no dirty submission was found. This prevents double
        // fetches, since we make the same call in the action for the cases
        // where a dirty submission was found.
        if (!wasDirtySubmissionFound && navigator.onLine) 
            this.fetchData(prodID, individualID);
    }

    /**
     * Clears out local storage and sets the user/prod ids before dispatching
     * the action to fetch our survey data.
     *
     * @param {Integer} prodID
     * @param {Integer} individualID
     */
    fetchData = (prodID, individualID) => {
        localStorage.clear();
        // Update the State with the user IDs
        this.registerSurveyIdsWithAppState(individualID, prodID);

        // Get our data!
        this.props.actions.fetchSurveyData(prodID, individualID);
    }

    /**
     * Clears out local storage and sets the user/prod ids before dispatching
     * the action to fetch our survey data.
     *
     * @param {String} encryptedSurveyParams
     * @param {String} token
     */
    fetchDataEncrypted = (encryptedParams, token) => {
        localStorage.clear();
        // Update the State with the user IDs
        this.registerEncryptedSurveyIdsWithAppState(encryptedParams);
        this.registerEncryptedTokenWithAppState(token);
        // Get our data!
        this.props.actions.fetchSurveyDataEncrypted(encryptedParams, token);
    }

    get shouldLoadBackupSurvey() {
        const encryptedParams = getQueryParams('e', false); // bool flag ensures that encrypted params aren't altered

        return encryptedParams || false;
    }

    get shouldRedirectToBackupSurvey() {
        const encryptedParams = getQueryParams('e', false); // bool flag ensures that encrypted params aren't altered

        return (this.shouldLoadBackupSurvey && this.props.encryptedIDs === encryptedParams) || false;  
    }

    render() {
        const {
            didFetchFail, 
            didDirtyPostFail, 
            didPostFail, 
            isDataFetched, 
            isDataFetching, 
            isDataPosting,
            isExpiredEval,
            surveyData
        } = this.props;
        const { apiDirtyPostError, apiGetServerError, apiPost500error, apiGetExpiredEvaluation } = globalData.errorMessages;
        const shouldLoaderShow = (isDataFetching || isDataPosting) && !didDirtyPostFail;
        const shouldStaysShow = isDataFetched && !isDataPosting && !didDirtyPostFail && !didPostFail && !didFetchFail;

        if (shouldStaysShow && surveyData.IsBackup) {
            return <Redirect to={"/NPS"} />;
        }

        return (
            <div className='home-container'>
                {didFetchFail && <Error errorText={apiGetServerError} />}

                {isExpiredEval && <Error errorText={apiGetExpiredEvaluation} />}

                {didDirtyPostFail && <Error errorText={apiDirtyPostError} />}

                {didPostFail && <Error errorText={apiPost500error} />}

                {shouldLoaderShow && <div className='loader' />}

                {shouldStaysShow && ( 
                    <section className='wrapper home'>
                        {surveyData.IsBackup && (
                            <Header customerData={surveyData.Evaluator} />
                        )}

                        <div className='container'>
                            {surveyData.IsBackup && (
                                <img 
                                    alt="Education First Logo" 
                                    className="home__logo"
                                    src="./build/media/EF-notag.svg" 
                                />
                            )}

                            <h1 className='home__heading'>
                                Welcome <br /> 
                                {surveyData.Evaluator.FirstName} {surveyData.Evaluator.LastName}
                            </h1>

                            <div 
                                className='home__intro-text'
                                dangerouslySetInnerHTML={{ __html: surveyData.IntroText }}
                            />

                            <LocationComponentList
                                actions={this.props.actions}
                                overallSubmission={surveyData.OverallSubmission} 
                                stays={surveyData.StaySubmissions}
                            />
                        </div>
                    </section>
				)}
            </div>
        );
    }
}


const mapStateToProps = (state) => {
    return {
        didFetchFail: state.surveyReducer.didFetchFail,
        didDirtyPostFail: state.surveyReducer.didDirtyPostFail,
        didPostFail: state.surveyReducer.didPostFail,
        individualID: state.dataReducer.individualID,
        encryptedIDs: state.dataReducer.encryptedIDs,
        isDataFetched: state.surveyReducer.isDataFetched,
        isDataFetching: state.surveyReducer.isDataFetching,
        isDataPosting: state.surveyReducer.isDataPosting,
        isExpiredEval: state.surveyReducer.isExpiredEval,
        prodID: state.dataReducer.prodTourID,
        surveyData: state.surveyReducer.surveyData
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        actions: bindActionCreators(Actions, dispatch)
    };
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Home));



// WEBPACK FOOTER //
// ./application/containers/Home/Home.jsx